/**
 * main — the studio boot entry. Boots inside a try/catch: a composition error
 * renders the boot-failure UI, not a blank screen. Composes the ONE studio
 * genome, resolves it through the SDK router (in App), and mounts the shell.
 *
 * The genome is composed HERE (inside the boot try/catch) via
 * `composeStudioGenome()` so a bad graph surfaces as the boot-failure diagnostic
 * rather than an uncaught module-load throw. Stylesheet order is explicit: the
 * SDK a11y/theme baseline first, then the studio theme tokens.
 *
 * C3 LIVE-RESTAGE (the Q3d studio binding): the genome is HOISTED into React
 * state (`StudioBoot`) and the `createRestage({ mode:'in-place', recompose,
 * onStaged: setGenome })` seam is bound + provided through `RestageProvider`, so
 * an agent (or the C3 panel) genome-edit re-composes → `setGenome(next)` →
 * `App`'s route memo re-runs and the affected route re-renders WITHOUT a reload.
 *
 * @module @ubiclouds/v5-studio/main
 */

import {
  StrictMode,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import { createRoot } from 'react-dom/client';
import type { GenomeV3 } from '@ubiclouds/v5-sdk/genome';
import type { ComposableGenomePlugin } from '@ubiclouds/v5-sdk/plugins';
import '@ubiclouds/v5-sdk/styles/base.css';
// #1845 — the `--os-syntax-*` family (the IDE editor's token colours). A SECOND
// token source because base.css sat at 471 of the 500-line gate-7 ceiling; the
// values are custom properties, so this rides the ENTRY CSS asset and costs the
// gate-8 entry-JS budget nothing.
import '@ubiclouds/v5-sdk/styles/syntax.css';
import './styles/theme.css';
// #865 S1 (gate 8): only the EAGER, no-validate `composeStudioGenome` is a static
// import. The VALIDATING recompose (`composeStudioGenomeFrom`) is used solely
// inside the lazy restage block below, so it is imported THERE — a static import
// here would pull `GenomeBuilderV3` (whose fluent methods Rollup cannot shake off
// an instantiated class) + the validator slot back onto the entry chunk.
import { composeStudioGenome, studioPlugins } from './genome/genome.js';
import { App } from './app/App.js';
import { AppErrorBoundary } from './app/error-boundary.js';
// BootFailure is EXCEPTIONAL-path UI — lazy-loaded in the boot catch below
// (#572 gate-8 reclaim; the error-boundary defers it the same way), so the
// eager entry never pays for the diagnostic surface.
import type { Restage } from '@ubiclouds/v5-agent-runtime/restage';
import { RestageProvider } from './agent/restage-context.js';
import type { InstallResult } from '@ubiclouds/v5-store/install';
import type { StoreArtifactManifest, Capability } from '@ubiclouds/v5-store/manifest';
import { StoreBootProvider } from '@ubiclouds/v5-store-ui/store-boot-context';
import { AuthGate } from './auth/AuthGate.js';

// #502 — the per-user prefs overlay is a LAZY split chunk: its live-transport chain
// (workspace operation manifest + contract schema registry) is heavy and must stay
// OFF the entry (gate 8). Rendered under Suspense at the boot root so `/agent` AND
// `/ide` both get the served overlay; the SYNC localStorage hydrate that makes
// config "ready before first run" happens at store creation, not here.
const PrefsOverlay = lazy(() => import('./ide/prefs/PrefsOverlay.js'));

// #590 — the app-lifetime automation rule driver, mounted at the BOOT root so
// schedules + device-state rules fire regardless of the active route (previously
// the driver only ticked while `/scenes` was mounted). LAZY (the #502 PrefsOverlay
// precedent): its device-adapter / scene-dispatch chain stays OFF the entry chunk
// (gate 8); it renders nothing (a refcounted lease carrier).
const AutomationHost = lazy(() => import('./home/AutomationHost.js'));

// #797 — the app-wide ephemeral toast render region (the `@ubiclouds/v5-ui-toolkit/chrome`
// `ToastHost` over the singleton controller). Boot-mounted so any `useToast().notify`
// (the git commit-succeeded ack, and future adopters) lands in one place. LAZY so the
// chrome-barrel pull stays OFF the eager entry chunk (gate 8 has ~0.4 KB headroom — a
// static import would consume it); renders nothing until a toast is raised.
const ToastHost = lazy(() =>
  import('@ubiclouds/v5-ui-toolkit/chrome').then((m) => ({ default: m.ToastHost })),
);
// #905 / #1068 (gate 8): the ENTIRE Store install/uninstall seam BODY —
// `install-engine` + `source-registrar`, the engine invocation, the capability
// grant + broker seeding, the ui-panel persist lane, and the SDK editor
// `GenomePluginRegistry` the install path registers into — lives behind a
// dynamic `import()` in `store/boot-store-seam.ts`. None of it can run before a
// user opens the LAZY `/store` surface and clicks Install, and the registry in
// particular is an INSTANTIATED CLASS (whose methods Rollup cannot shake, the
// #865 lesson) that only the install path reads. `StudioBoot` keeps the state +
// the `StoreBootContext`; the callbacks are thin async trampolines. Both are
// already `async` and already `await loadInstallEngine()`, so the UX is
// unchanged (the seam resolves inside the await the engine already needed).
// #1272 (gate 8, module axis) — `build-sha.ts` is DYNAMIC-imported at its single
// call site in `mount()` rather than statically pulled onto the eager entry. Its
// two exports are a pure precedence helper and a one-line `dataset` write, both
// used exactly once, at boot, inside an already-`async` function — so the stamp
// lands a microtask later, still before `createRoot(...).render(...)`. EVICTION,
// not a merge: the module keeps its own home and its own coverage (the #1272
// budget note records why merging already-eager modules is the gaming hazard the
// module axis exists to catch).

// The build-stamp inlined by Vite's `define` (#240): the resolved short SHA
// (env → git → 'dev'). `typeof` guards a build that didn't inline it.
declare const __BUILD_SHA__: string;

/**
 * The boot shell: holds the LIVE genome in state, binds the in-place restage
 * seam (recompose = re-compose the studio genome; onStaged = `setGenome`), and
 * provides the boot agent context to the `/agent` console (C3).
 */
function StudioBoot({
  initial,
  authEnabled,
}: {
  readonly initial: GenomeV3;
  /**
   * The resolved Auth0 login-wall flag (#1272), read ONCE in `mount()` after
   * `loadRuntimeConfig()` and threaded to {@link AuthGate}. Keeping the read at
   * the boot — where the config has just been awaited — evicts
   * `auth/auth-enabled.ts` and `config/runtime-config.ts` from the eager entry
   * closure (gate 8, module axis): the flag module is still the SINGLE
   * definition, it is just dynamic-imported here instead of statically imported
   * by `AuthGate`. Defaults to the off-by-default contract at the prop.
   */
  readonly authEnabled: boolean;
}): ReactElement {
  const [genome, setGenome] = useState<GenomeV3>(initial);
  // The plugin list `App` resolves routes from is hoisted too (#116d V2): a Store
  // install appends the installed plugin so `resolveRoutes(genome, plugins)` finds
  // its editor and the new route renders in-session (no reload).
  const [plugins, setPlugins] = useState<readonly ComposableGenomePlugin[]>(studioPlugins);
  const [installedIds, setInstalledIds] = useState<readonly string[]>([]);

  // #705 S3 — the FULL 16-kind genome for the genome-graph panel + generated-index
  // sibling discovery (the `StudioGenomeProvider` value). The router boots the eager
  // ROUTE-ONLY `genome` (schema-free — no `generate()`/schema literals on the entry),
  // so the full data-flow graph is composed LAZILY here (`genome-full.js`, off the
  // entry) and threaded to `App` as `graphGenome`. `null` until the microtask
  // resolves; `App` falls back to the route-only `genome` in the meantime (its route
  // table is a superset, so sibling discovery is correct either way).
  const [graphGenome, setGraphGenome] = useState<GenomeV3 | null>(null);
  useEffect(() => {
    let alive = true;
    // #1106 AC7 — SEED-ONLY: an install can commit a fresher full genome before
    // this one-time load resolves; the boot value must never overwrite it.
    void import('./genome/genome-full.js').then(({ studioGenome }) => {
      if (alive) setGraphGenome((prev) => prev ?? studioGenome);
    });
    return () => {
      alive = false;
    };
  }, []);

  // Mirror the CURRENT hoisted plugin set into a ref so the (stable) restage seam
  // can read it WITHOUT re-creating the seam on every `plugins` change (#132). The
  // ref read is a plain `.current` deref in the recompose closure — NOT a
  // render-time `useSyncExternalStore` selector — so it sidesteps the React 19
  // `Object.is` loop while keeping `recompose` keyed to the live (post-install) set.
  const pluginsRef = useRef(plugins);
  pluginsRef.current = plugins;

  // #337 — the in-place restage seam is LAZY-loaded off the entry (mirrors the #279
  // `loadInstallEngine` recipe below): `@ubiclouds/v5-agent-runtime/restage{,-store}`
  // is ~2.5 KB-gz of agent-only code that nothing but the LAZY `/agent` surfaces
  // consume (`useRestage`/`useAgentBoot`), yet it was eagerly in the studio entry —
  // pushing gate-8 over the deploy-container ceiling. We import it on mount (a
  // microtask) and provide a `null` boot context until it resolves; the restage
  // consumers are already `null`-tolerant ("restage unavailable" degraded state), and
  // routing reads `genome` directly (App prop), not this context — so the brief
  // pre-load window is invisible in practice (the `/agent` chunk loads after this).
  const [restage, setRestage] = useState<Restage | null>(null);
  useEffect(() => {
    let alive = true;
    // #1068 — the ref CLEAR captured from the same lazy module the publish came
    // from, so the effect's cleanup needs no eager import of `restage-ref`.
    let clearRestageRef: (() => void) | undefined;
    void Promise.all([
      import('@ubiclouds/v5-agent-runtime/restage'),
      import('@ubiclouds/v5-agent-runtime/restage-store'),
      // #484e/#1705 — the recompose POLICY module (the payload apply +, since
      // #1705, the FULL-phase graph refresh). Its static imports —
      // `restage-payload.js` (js-yaml via genome-yaml), `genome-full.js` (the
      // validating compose) and `full-genome-refresh.js` — all ride the same
      // lazy chunk graph this block always carried, so nothing joins the eager
      // entry closure (gate 8, both axes).
      import('./genome/restage-recompose.js'),
      // #1068 (gate 8) — the studio-side `restage-ref` bridge is only ever
      // WRITTEN from inside this already-lazy block (its READER is the agent
      // chunk), so importing it here instead of statically evicts it — and the
      // shared `agent-ref-bridge` primitive — from the eager entry closure.
      import('./agent/restage-ref.js'),
    ]).then(([
      { createRestage },
      { bindRestageStore },
      { createRestageRecompose },
      { setRestageRef },
    ]) => {
      if (!alive) return;
      const seam = createRestage({
        mode: 'in-place',
        // The studio recompose policy (`genome/restage-recompose.ts`): reads the
        // CURRENT hoisted plugin set via `pluginsRef` (#132 — never the frozen
        // `studioPlugins` base, so an edit AFTER a Store install recomposes WITH
        // the installed plugin), applies a payload additively (#484e), and — #1705
        // — commits the plugin list AND the FULL 16-kind graph genome in the SAME
        // React batch as `onStaged` → `setGenome`, fail-CLOSED: a full-phase
        // refusal throws a staged error, the seam answers `{status:'error'}`, and
        // NOTHING stages (route table, graph, mint all untouched).
        recompose: createRestageRecompose({
          currentPlugins: () => pluginsRef.current,
          commitPlugins: setPlugins,
          commitGraphGenome: setGraphGenome,
        }),
        onStaged: (next) => setGenome(next),
        emit: bindRestageStore(),
      });
      setRestage(seam);
      // Publish the seam into the studio-side restage-ref so the agent's `ide.restage`
      // intent (#346b-r) can drive it; cleared on cleanup so a stale seam never fires.
      setRestageRef(seam);
      clearRestageRef = () => setRestageRef(null);
    });
    return () => {
      alive = false;
      clearRestageRef?.();
    };
  }, []);

  // `null` until the lazy restage seam resolves — the boot context type requires both
  // genome + restage, and the agent surfaces handle a `null` context gracefully.
  const bootValue = useMemo(
    () => (restage ? { genome, restage } : null),
    [genome, restage],
  );

  // The live Store install/uninstall seam: run the REAL #116c engine against the
  // live genome, then commit the recomposed genome + the resolved plugin so the
  // installed route goes live (install) / disappears (uninstall) in-session.
  //
  // The compose ENGINE is render-free (only these callbacks call it), so it is
  // lazy-imported off the entry (#279 — `loadInstallEngine`, memoised; warmed by
  // the lazy `/store` surface's `prefetchInstallEngine()` on mount). The callbacks
  // are async — the `/store` surface awaits the recompose before showing its
  // result, so the no-reload install UX is unchanged (just a microtask later).
  const install = useCallback(
    async (
      manifest: StoreArtifactManifest,
      confirm: boolean,
      approvedCapabilities?: readonly Capability[],
    ): Promise<InstallResult> => {
      const { runInstall } = await import('@ubiclouds/v5-store-ui/boot-store-seam');
      // #1702 — the remote lane's HOST-MODULE source, registered as a THUNK so the
      // studio's own react/three instances (host-modules-boot's static imports)
      // load only if an install actually goes remote. Idempotent — last writer
      // wins, the publisher's own HMR contract — so per-call registration is the
      // cheapest correct placement: no eager-entry cost, no boot-order coupling.
      const { setHostModuleProvider } = await import('@ubiclouds/v5-store-ui/remote-env');
      setHostModuleProvider(async () => {
        const boot = await import('./genome/host-modules-boot.js');
        return boot.bootHostModules();
      });
      const result = await runInstall(
        // #1097 — `hostPlugins` is read from `pluginsRef` (the #132 always-current
        // mirror), NOT from the `plugins` state closed over by this `[genome]`-keyed
        // callback: a captured list goes stale the moment a previous install lands,
        // and the engine's route pre-flight would then FALSE-REFUSE the next one.
        { genome, setGenome, setPlugins, setInstalledIds, hostPlugins: pluginsRef.current },
        manifest,
        confirm,
        approvedCapabilities,
      );
      // #1106 — re-derive the FULL 16-kind genome (lazy, fail-soft) so the graph
      // panel, the `genomeGraph` PERCEIVE and the `graph.*` ACT cache see the
      // installed namespace. `ui-panel` installs (zero entities — incl. the boot
      // ui-install-replay) skip even the import. A refresh failure never fails
      // the already-committed install (the module warns naming the artifact).
      if (manifest.kind !== 'ui-panel') {
        const refreshed = await import('./genome/full-genome-refresh.js')
          .then((m) => m.refreshAfterInstall(manifest.kind, result.plugin, manifest.id))
          .catch(() => null);
        if (refreshed !== null) setGraphGenome(refreshed);
      }
      return result;
    },
    [genome],
  );

  const uninstall = useCallback(
    async (manifest: StoreArtifactManifest): Promise<InstallResult> => {
      const { runUninstall } = await import('@ubiclouds/v5-store-ui/boot-store-seam');
      // #1097 — the live plugin list, via the #132 ref (see `install` above).
      const result = await runUninstall(
        { genome, setGenome, setPlugins, setInstalledIds, hostPlugins: pluginsRef.current },
        manifest,
      );
      // #1106 — symmetric full-genome refresh over the REMAINING installed set.
      if (manifest.kind !== 'ui-panel') {
        const refreshed = await import('./genome/full-genome-refresh.js')
          .then((m) => m.refreshAfterUninstall(manifest.kind, result.pluginId, manifest.id))
          .catch(() => null);
        if (refreshed !== null) setGraphGenome(refreshed);
      }
      return result;
    },
    [genome],
  );

  // #376 — replay persisted ui-panel installs ONCE at boot (lazy module — the
  // persist/index chain stays off the entry). The replay re-applies each entry's
  // RECORDED trust confirm + capability grant through the SAME `install` seam
  // (reload is NOT a new trust decision — `store/ui-install-replay.ts` states
  // the posture); the mount + broker + `installedIds` re-materialise, so a
  // persisted `store-ui:*` IDE tab resolves again. Ref-guarded to one run.
  const uiReplayRef = useRef(false);
  useEffect(() => {
    if (uiReplayRef.current) return;
    uiReplayRef.current = true;
    void import('@ubiclouds/v5-store-ui/ui-install-replay').then(async ({ replayUiInstalls }) => {
      const outcome = await replayUiInstalls(install);
      // #1672 — a skipped replay used to be SILENT: the per-entry `catch {}`
      // discarded the reason, so a persisted panel VANISHED on reload with nothing
      // anywhere saying why. The engines gate is exactly the refusal that arrives
      // this way (fleet-wide, on an SDK major bump), so every skip is surfaced with
      // the gate that produced it. `warn`, not `error`: a refusal must never block
      // a boot, and the CDP smoke's zero-console-errors assertion is about real
      // failures, not about an artifact this host legitimately declines.
      for (const skip of outcome.skipped) {
        console.warn(
          `[store] persisted panel "${skip.artifactId}" did not come back this boot ` +
            `(${skip.word}${skip.code ? `/${skip.code}` : ''}): ${skip.reason}`,
        );
      }
    });
  }, [install]);

  const storeValue = useMemo(
    () => ({ genome, install, uninstall, installedIds }),
    [genome, install, uninstall, installedIds],
  );

  // #501 — PUBLISH the live Store install/uninstall seam into the studio-side
  // `store-ref` bridge so the agent's `store.install`/`store.uninstall` intents
  // (pure runtime, no React import) drive the REAL #116c engine. Republishes on
  // every install/uninstall (installedIds changes) so the `storeSnapshot` PERCEIVE
  // sees the current installed set; the unmount cleanup clears the ref (no stale
  // seam).
  //
  // #1068 (gate 8) — `store-ref` is WRITTEN only here (its READER is the agent
  // chunk), so it is dynamic-imported rather than statically pulled onto the
  // eager entry. The CLEAR is a separate unmount-only effect: an async clear
  // inside the deps-keyed cleanup could otherwise resolve after the next
  // publish's import and null a live seam.
  useEffect(() => {
    let alive = true;
    void import('./agent/store-ref.js').then(({ setStoreRef }) => {
      if (alive) setStoreRef({ install, uninstall, installedIds });
    });
    return () => {
      alive = false;
    };
  }, [install, uninstall, installedIds]);
  useEffect(
    () => () => {
      void import('./agent/store-ref.js').then(({ setStoreRef }) => setStoreRef(null));
    },
    [],
  );

  return (
    <AuthGate enabled={authEnabled}>
      <RestageProvider value={bootValue}>
        <StoreBootProvider value={storeValue}>
          {/* #502 — HOIST the #375a per-user prefs overlay to the boot root so BOTH
              the standalone `/agent` console AND `/ide` get it (IdeShell mounts it
              too — a double-mount is safe: `setPrefs` is idempotent). Lazy + null
              fallback keeps its heavy live-transport chain off the entry (gate 8). */}
          <Suspense fallback={null}>
            <PrefsOverlay />
            <AutomationHost />
            <ToastHost />
          </Suspense>
          {/* #518 — boot safety for the NO-VALIDATE eager compose: a bad ref that
              survives the un-validated genome would throw during route resolution
              / render; the boundary renders BootFailure instead of a blank screen. */}
          <AppErrorBoundary>
            <App genome={genome} plugins={plugins} graphGenome={graphGenome} />
          </AppErrorBoundary>
        </StoreBootProvider>
      </RestageProvider>
    </AuthGate>
  );
}

async function mount(): Promise<void> {
  // Boot-stamp (#240, before the genome compose + outside its try/catch): the
  // Vite-inlined SHA (`__BUILD_SHA__`) is run through the covered helper —
  // `resolveBuildSha` re-applies the env → git → 'dev' precedence on the inlined
  // value (a build that didn't inline it falls through to 'dev'), then it's
  // logged + stamped onto `<html data-build-sha>` so a deployed bundle is
  // identifiable from the console + DOM. The logic lives in `build-sha.ts`.
  const { resolveBuildSha, stampBuildSha } = await import('./build-sha.js');
  const buildSha = resolveBuildSha({
    envSha: typeof __BUILD_SHA__ === 'string' ? __BUILD_SHA__ : undefined,
  });
  console.info('[v5-studio] build ' + buildSha);
  stampBuildSha(buildSha);

  const host = document.getElementById('root');
  if (!host) throw new Error('studio boot: #root mount node is missing from index.html');
  const root = createRoot(host);

  // Kick the deferred, off-entry dev-harness work at the very start of boot
  // (#226, fire-and-forget, before first paint): (1) register the `/dev` harness
  // SOURCES (Todo App + graded tiers) — resolves before the lazy `DevCanvas`
  // chunk can mount and read the source registry; (2) prefetch the Store-rail
  // source registrar. Both drag the harness's heavy stage/fixture/js-yaml chain,
  // which is `/dev`/install-only — keeping it out of the eager entry (gate 8).
  void import('./genome/dev-harness-boot.js');
  // #905 — the source-registrar seam is dynamic-imported (off the eager entry);
  // this fire-and-forget boot prefetch lands its holder before any /store install
  // click (`env.source` is OPTIONAL, so a not-yet-resolved read is safe).
  void import('@ubiclouds/v5-store-ui/source-registrar').then((m) => m.prefetchSourceRegistrar());

  try {
    // #572 — the runtime config is loaded BEFORE compose/render: one tiny
    // no-store GET of /config.json overlaying the build-time env seed, FAIL-SOFT
    // (absent/invalid → the seed alone → mock-first byte-identical), so every
    // config read after this point (all render/select-time) sees the served
    // values — including AuthGate's very first `isAuthEnabled()`. The loader is
    // DYNAMIC-imported: its schema + parseAs chain stays off the entry chunk
    // (gate 8); only the tiny synchronous `readConfig` cache is entry-eager.
    const { loadRuntimeConfig } = await import('@ubiclouds/v5-workbench/runtime-config-load');
    await loadRuntimeConfig();
    // #1272 (gate 8, module axis) — resolve the auth-wall flag HERE, from the
    // SAME `auth/auth-enabled.ts` leaf as before, now dynamic-imported. That
    // import was `AuthGate`'s only reason to be a static reader of the flag, and
    // the flag's only reason to drag `config/runtime-config.ts` onto the eager
    // entry: two modules off the first-paint closure for a value the boot has
    // just finished awaiting. Single definition, single read, unchanged
    // precedence — the flag was already boot-constant by construction.
    const { isAuthEnabled } = await import('./auth/auth-enabled.js');
    const authEnabled = isAuthEnabled();
    const genome = composeStudioGenome();
    root.render(
      <StrictMode>
        <StudioBoot initial={genome} authEnabled={authEnabled} />
      </StrictMode>,
    );
  } catch (error) {
    // A composition error renders the boot-failure UI, never a blank screen.
    // The diagnostic surface is a lazy chunk (#572 gate-8 reclaim); if even
    // that import fails (network gone mid-boot) fall back to a plain-DOM
    // message so the screen is STILL never blank.
    try {
      const { BootFailure } = await import('./app/boot-failure.js');
      root.render(
        <StrictMode>
          <BootFailure error={error} />
        </StrictMode>,
      );
    } catch {
      console.error('[v5-studio] boot failed', error);
      host.textContent = 'studio boot failed — see the console for details';
    }
  }
}

void mount();
